import { LOG_THEME, LOG_THEME_SCHEMA } from '../constants/enums'
import { isDarkMode } from '.'

import env from 'env'

export const logStyles = (bg: string, color: string, color2?: string, border = 'transparent', borderRaduis = '2px'): string[] =>
  [
    `background: ${bg}; color: ${color}; display: inline-block; padding: 2px 4px; border-radius: ${borderRaduis}; border: 1px solid ${border};`,
    `color: ${color2 || isDarkMode() ? '#fff' : '#000'}; font-weight: bold;`
  ]

export class Logger implements LOGGER {
  private readonly console = window['console']
  name: string

  static get COLORS() {
    return {
      RED: '#D92824',
      YELLOW: '#caa400',
      ORANGE: '#FF6700',
      BLACK: '#000000',
      GREY: '#F9F9F9',
      GREEN: '#28a745',
      BLUE: '#0466c8',
      CYAN: '#6AB3D9',
      VIOLET: '#9980ff',
      WHITE: '#ffffff'
    }
  }

  static get TIME() {
    const date = new Date
    return `[${Logger.pad(date.getHours())}:${Logger.pad(date.getMinutes())}]`
  }

  static pad(value: number) {
    return ('0' + value).slice(-2)
  }

  constructor(silent = false, name = 'sdk') {
    this.name = name

    if (silent) {
      // @ts-expect-error: ignore
      this.console = new Proxy({}, { get() { return () => undefined } })
    }
  }

  get RED() {
    return this.#logTypes(Logger.COLORS.RED)
  }

  get ORANGE() {
    return this.#logTypes(Logger.COLORS.ORANGE)
  }

  get BLUE() {
    return this.#logTypes(Logger.COLORS.BLUE)
  }

  get CYAN() {
    return this.#logTypes(Logger.COLORS.CYAN)
  }

  get BLACK() {
    return this.#logTypes(Logger.COLORS.BLACK)
  }

  get GREEN() {
    return this.#logTypes(Logger.COLORS.GREEN)
  }

  get VIOLET() {
    return this.#logTypes(Logger.COLORS.VIOLET)
  }

  get YELLOW() {
    return this.#logTypes(Logger.COLORS.YELLOW, Logger.COLORS.BLACK, '#a36b04')
  }

  #logTypes<T>(color: string, color2?: string, fillColor?: string) {
    const log = (style: LOG_THEME_SCHEMA) => (type: string, event: string, data?: T) => this.COLOR(type, event, data, style)

    return {
      OUTLINE: log({ bg: 'transparent', color, border: color }),
      SOLID: log({ bg: color, color: Logger.COLORS.WHITE }),
      FILL: log({ bg: color, color: fillColor || Logger.COLORS.WHITE, color2 }),
      TEXT: log({ bg: 'none', color })
    }
  }

  BOOL(value: boolean) {
    return value ? 'Yes' : 'No'
  }

  COLOR(type: string, event: string, data: unknown, style = LOG_THEME) {
    const styles = logStyles(style.bg, style.color, style.color2, style.border)
    this.console.log(`%c ${type} %c ${event}`, styles[ 0 ], styles[ 1 ], Logger.TIME, data !== undefined ? data : '')
  }

  TABLE<T>(data: T, props?: string[]): void {
    this.console.table.call(null, data, props)
  }

  LOG(...args: unknown[]): void {
    this.console.log.apply(null, args)
  }

  WARNING(...args: unknown[]): void {
    this.console.warn.apply(null, args)
  }

  INFO(...args: unknown[]): void {
    this.console.info.apply(null, [`%cinfo:`, 'color:blue;', ...args])
  }

  ERROR(...args: unknown[]): void {
    this.console.error.apply(null, args)
  }

  GROUP_START(...args: unknown[]): void {
    this.console.groupCollapsed.apply(null, args)
  }

  GROUP_END(): void {
    this.console.groupEnd()
  }
}

export default new Logger(!env?.LOG)
